import { rpcCall } from '@scayle/storefront-nuxt'
import type {
  NewsletterSubscriptionData,
  MinimalNewsletterSubscriptionData,
} from '~/rpcMethods/newsletter'
import type { Locale } from '~/config/locales'
import { getLocaleIsoCode } from '~/config/locales'

const useNewsletter = () => {
  const isUnsubscribed = ref(false)
  const fetching = ref(false)
  const error = ref()
  const done = ref(false)
  const currentShop = useCurrentShop()
  const nuxtApp = useNuxtApp()

  const unsubscribe = async (email: string) => {
    try {
      error.value = null
      fetching.value = true

      const call = rpcCall(nuxtApp, 'unsubscribeNewsletter', currentShop.value)

      await call({
        email,
        businessUnit: getLocaleIsoCode(currentShop.value.locale as Locale),
      })

      isUnsubscribed.value = true
    } catch (err) {
      error.value = err
      isUnsubscribed.value = false
    }

    fetching.value = false
  }

  const unsubscribeTransaction = async (email: string) => {
    try {
      error.value = null
      fetching.value = true

      const call = rpcCall(
        nuxtApp,
        'unsubscribeTransactionNewsletter',
        currentShop.value,
      )

      await call({
        email,
        businessUnit: getLocaleIsoCode(currentShop.value.locale as Locale),
      })

      isUnsubscribed.value = true
    } catch (err) {
      error.value = err
      isUnsubscribed.value = false
    }

    fetching.value = false
  }

  const subscribe = async (
    data: Omit<NewsletterSubscriptionData, 'businessUnit'>,
  ) => {
    try {
      error.value = null
      fetching.value = true
      done.value = false

      const call = rpcCall(nuxtApp, 'subscribeNewsletter', currentShop.value)

      await call({
        businessUnit: getLocaleIsoCode(currentShop.value.locale as Locale),
        ...data,
      })

      done.value = true
    } catch (err) {
      error.value = err
    }

    fetching.value = false
  }

  // Email-only signup for the NewsletterRegisterWidget: hits the dedicated
  // name-optional RPC (ECOM-2758/2803) so the legacy `subscribe` above keeps
  // requiring a name.
  const subscribeMinimal = async (
    data: Omit<MinimalNewsletterSubscriptionData, 'businessUnit'>,
  ) => {
    try {
      error.value = null
      fetching.value = true
      done.value = false

      const call = rpcCall(
        nuxtApp,
        'subscribeNewsletterMinimal',
        currentShop.value,
      )

      await call({
        businessUnit: getLocaleIsoCode(currentShop.value.locale as Locale),
        ...data,
      })

      done.value = true
    } catch (err) {
      error.value = err
    }

    fetching.value = false
  }

  return {
    subscribe,
    subscribeMinimal,
    isUnsubscribed,
    unsubscribe,
    unsubscribeTransaction,
    fetching,
    error,
    done,
  }
}

export default useNewsletter
